import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TopBar } from "../components/TopBar";
import { api, ApiError } from "../lib/api";
import { ErrorText } from "../components/ui";
import { shortDateTime, visibleEmail } from "../lib/format";

type LevelInfo = { level: number; label: string; xp_in_level: number; xp_to_next: number; progress_pct: number; is_max: boolean };

type StudentDetail = {
  profile: {
    id: number;
    name: string;
    email?: string | null;
    login_id?: string | null;
    locale: string;
    total_xp: number;
    current_level: number;
    coin_balance: number;
    streak_days: number;
    last_studied_at: string | null;
    unlocked_areas: string[];
    earned_badges: string[];
    level: LevelInfo;
  };
  attempts: Attempt[];
  portfolio: PortfolioEntry[];
  feedback: Feedback[];
};

type Attempt = {
  id: number;
  area_id: string;
  task_id: string;
  is_correct: number;
  selected: string;
  attempted_at: string;
  xp_awarded: number;
};

type PortfolioEntry = {
  id: number;
  area_id: string;
  task_id: string | null;
  type: string;
  payload_json: string;
  created_at: string;
};

type Feedback = {
  id: number;
  feedback_text: string;
  teacher_name: string;
  created_at: string;
};

export function StudentDetailPage() {
  const { t } = useTranslation(["common", "admin"]);
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [feedbackText, setFeedbackText] = useState("");
  const [sending, setSending] = useState(false);
  const [sentMsg, setSentMsg] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["teacher-student", id],
    queryFn: () => api<StudentDetail>(`/api/teacher/students/${id}`),
    enabled: !!id,
    retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 1,
  });

  async function sendFeedback() {
    if (!feedbackText.trim() || !id) return;
    setSending(true);
    setSentMsg(null);
    try {
      await api("/api/teacher/students/feedback", {
        method: "POST",
        body: { learner_id: Number(id), feedback_text: feedbackText },
      });
      setFeedbackText("");
      setSentMsg(t("admin:feedback_sent"));
      void queryClient.invalidateQueries({ queryKey: ["teacher-student", id] });
    } catch {
      setSentMsg(t("common:error_generic"));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar subtitle={data?.profile.name} />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/" className="text-sm text-muted underline">{t("common:back")}</Link>

        {isLoading && <p className="text-muted mt-4">{t("common:loading")}</p>}
        {/* 担当外（403/404）は中身を出さない */}
        {error && <div className="mt-4"><ErrorText>{t("admin:student_unavailable")}</ErrorText></div>}

        {data && (
          <>
            <div className="mt-4 bg-white rounded-2xl shadow-sm p-6 flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="text-xs text-muted">{data.profile.login_id ?? visibleEmail(data.profile.email)}</div>
                <h1 data-testid="student-name" className="text-2xl font-bold text-primary">{data.profile.name}</h1>
                {data.profile.earned_badges.length > 0 && (
                  <div className="text-sm text-ops mt-2">
                    {data.profile.earned_badges.map(badgeLabel).join("・")}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-2xl font-bold text-primary">{data.profile.level?.label ?? "入門"}</div>
                  <div className="text-xs text-muted">{t("admin:level")}（{data.profile.level?.progress_pct ?? 0}%）</div>
                </div>
                <Stat label="XP" value={data.profile.total_xp} />
                <Stat label={t("admin:coins")} value={data.profile.coin_balance} />
                <Stat label={t("admin:streak")} value={`${data.profile.streak_days}${t("admin:days")}`} />
              </div>
            </div>

            <section className="mt-8">
              <h2 className="text-lg font-bold text-primary mb-3">{t("admin:recent_activity")}</h2>
              {data.attempts.length === 0 ? (
                <p className="text-muted text-sm">{t("admin:no_recent_activity")}</p>
              ) : (
                <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs text-muted">
                      <tr>
                        <th className="text-left px-4 py-2">{t("admin:col_task")}</th>
                        <th className="text-left px-4 py-2">{t("admin:col_answer")}</th>
                        <th className="text-right px-4 py-2">{t("admin:col_result")}</th>
                        <th className="text-right px-4 py-2">XP</th>
                        <th className="text-right px-4 py-2">{t("admin:col_date")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.attempts.map((a) => (
                        <tr key={a.id} data-testid="student-attempt-row" className="border-t border-gray-100">
                          <td className="px-4 py-2 font-mono text-xs">{a.task_id}</td>
                          <td className="px-4 py-2 font-mono text-xs">{a.selected}</td>
                          <td className="px-4 py-2 text-right">
                            {a.is_correct ? <span className="text-green-600">○</span> : <span className="text-accent">×</span>}
                          </td>
                          <td className="px-4 py-2 text-right">+{a.xp_awarded}</td>
                          <td className="px-4 py-2 text-right text-muted text-xs">{shortDateTime(a.attempted_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="mt-8">
              <h2 className="text-lg font-bold text-primary mb-3">{t("admin:feedback_title")}</h2>
              <div className="bg-white rounded-2xl shadow-sm p-4">
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder={t("admin:feedback_placeholder")}
                  rows={4}
                  className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:border-primary focus:outline-none"
                />
                <div className="flex items-center justify-between mt-2">
                  {sentMsg && <span className="text-sm text-green-600">{sentMsg}</span>}
                  <button
                    type="button"
                    disabled={sending || !feedbackText.trim()}
                    onClick={sendFeedback}
                    className="ml-auto bg-ops text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50"
                  >
                    {sending ? t("common:loading") : t("admin:send_feedback")}
                  </button>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                {data.feedback.length === 0 ? (
                  <p className="text-muted text-sm">{t("admin:no_feedback_yet")}</p>
                ) : (
                  data.feedback.map((f) => (
                    <div key={f.id} className="bg-white border border-gray-100 rounded-xl p-4">
                      <div className="text-xs text-muted">{f.teacher_name} 先生 / {shortDateTime(f.created_at)}</div>
                      <div className="text-sm text-body mt-2 whitespace-pre-wrap">{f.feedback_text}</div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="text-xl font-bold text-primary">{value}</div>
      <div className="text-xs text-muted">{label}</div>
    </div>
  );
}

function badgeLabel(key: string): string {
  const map: Record<string, string> = {
    hiragana_master: "ひらがな",
    katakana_master: "カタカナ",
    streak_7d: "連続7日",
    streak_30d: "連続30日",
  };
  return map[key] ?? key;
}
